import { Button, PdfViewer, Spinner } from '@eureca/ui';
import type { DocumentKind } from './document-viewer-query';
import { ShareDocumentDialog, type ShareDocumentDialogProps } from './ShareDocumentDialog';
import { FileSearchIcon } from './icons';

/** Estado do download do PDF (bytes via proxy autenticado). */
export type DocumentViewerState = 'loading' | 'error' | 'not-found' | 'ready';

export interface DocumentViewerPageProps {
  kind: DocumentKind;
  /** socialName ?? name — null enquanto o perfil carrega. */
  candidateName: string | null;
  candidateEmail: string | null;

  state: DocumentViewerState;
  /** Object URL do PDF baixado — presente só em `state === 'ready'`. */
  documentUrl: string | null;
  onRetry: () => void;
  /**
   * EU-752 — o PDF.js não conseguiu renderizar os bytes (objeto corrompido /
   * não-PDF). O container vira o NOSSO estado de erro: nunca o fallback
   * "Abrir PDF" do PdfViewer com `Compartilhar` habilitado por cima.
   */
  onPdfLoadError: () => void;
  /** Volta ao perfil do candidato preservando o companyId. */
  onBack: () => void;
  onOpenShare: () => void;

  /** Modal Compartilhar — estado no container (PFC-022..027). */
  isShareOpen: boolean;
  onShareOpenChange: (open: boolean) => void;
  ttlHours: number | null;
  onTtlChange: ShareDocumentDialogProps['onTtlChange'];
  shareUrl: string | null;
  shareExpiryLabel: string | null;
  isGenerating: boolean;
  onGenerateLink: () => void;
  onCopyLink: () => void;
}

const KIND_LABEL: Record<DocumentKind, string> = {
  resume: 'Currículo',
  attestation: 'Laudo',
};

const NOT_FOUND_COPY: Record<DocumentKind, string> = {
  resume: 'Este candidato não tem currículo cadastrado.',
  attestation: 'Este candidato não tem laudo cadastrado.',
};

/**
 * Visualizador de currículo/laudo do candidato (EU-594 PFC-017..021) — Figma
 * "Profile / Resume" (15539:56608) e "Profile / Report" (15539:56629).
 *
 * Header FLUTUANTE no topo da área do documento (a área do PDF rola por
 * baixo; o header fica visível durante o scroll): `{Currículo|Laudo}: {nome}
 * | {e-mail}` à esquerda, Compartilhar (primário) + Voltar à direita. O PDF
 * ocupa toda a área útil restante (todas as páginas, fit-width, fundo
 * neutro). Puro: sem hooks, sem router.
 */
export function DocumentViewerPage({
  kind,
  candidateName,
  candidateEmail,
  state,
  documentUrl,
  onRetry,
  onPdfLoadError,
  onBack,
  onOpenShare,
  isShareOpen,
  onShareOpenChange,
  ttlHours,
  onTtlChange,
  shareUrl,
  shareExpiryLabel,
  isGenerating,
  onGenerateLink,
  onCopyLink,
}: DocumentViewerPageProps) {
  const identity = [candidateName, candidateEmail].filter(Boolean).join(' | ');

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {/* Header flutuante — banda fixa; só a área do documento rola. */}
      <header className="z-10 flex flex-none flex-row items-center justify-between gap-4 border-b border-border bg-card py-5 pl-8 pr-8 shadow-sm md:pl-16">
        <p className="min-w-0 truncate text-sm text-foreground" data-testid="document-viewer-title">
          <span className="font-semibold">{KIND_LABEL[kind]}: </span>
          {identity}
        </p>
        <div className="flex flex-none flex-row items-center gap-2">
          <Button
            type="button"
            onClick={onOpenShare}
            disabled={state !== 'ready'}
            data-testid="document-viewer-share"
          >
            Compartilhar
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            data-testid="document-viewer-back"
          >
            Voltar
          </Button>
        </div>
      </header>

      {/* Área do documento — fundo neutro, PDF fit-width com rolagem própria. */}
      <div className="flex min-h-0 flex-1 flex-col bg-neutral-300">
        {state === 'loading' && (
          <div
            className="flex flex-1 items-center justify-center"
            data-testid="document-viewer-loading"
          >
            <Spinner className="size-8 text-muted-foreground" />
          </div>
        )}

        {state === 'error' && (
          <div
            className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center"
            data-testid="document-viewer-error"
          >
            <p className="text-sm text-foreground">
              Não foi possível carregar o documento. Tente novamente.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={onRetry}
              data-testid="document-viewer-retry"
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {state === 'not-found' && (
          <div
            className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center"
            data-testid="document-viewer-not-found"
          >
            <FileSearchIcon className="size-10 text-muted-foreground" />
            <div className="flex flex-col gap-1">
              <p className="text-base font-medium text-foreground">Documento não encontrado</p>
              <p className="text-sm text-muted-foreground">{NOT_FOUND_COPY[kind]}</p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              data-testid="document-viewer-not-found-back"
            >
              Voltar ao perfil
            </Button>
          </div>
        )}

        {state === 'ready' && documentUrl && (
          <PdfViewer
            url={documentUrl}
            title={`${KIND_LABEL[kind]} do candidato`}
            className="min-h-0 flex-1 bg-neutral-300"
            // EU-752 — render falhou → estado de erro da página (com "Tentar
            // novamente"), não o botão "Abrir PDF" dentro de um `ready`.
            fallback="none"
            onLoadError={onPdfLoadError}
            data-testid="document-viewer-pdf"
          />
        )}
      </div>

      <ShareDocumentDialog
        kind={kind}
        open={isShareOpen}
        onOpenChange={onShareOpenChange}
        ttlHours={ttlHours}
        onTtlChange={onTtlChange}
        shareUrl={shareUrl}
        shareExpiryLabel={shareExpiryLabel}
        isGenerating={isGenerating}
        onGenerateLink={onGenerateLink}
        onCopyLink={onCopyLink}
      />
    </div>
  );
}
